import { ID_PART_PATTERN } from "./id-grammar.js";
import { assertValidDurableName, eventStreamName } from "./event-subjects.js";
import { WORK_NAME_PATTERN } from "./work-name.js";
import { z } from "zod";
//#region src/work-subjects.ts
/**
* Maximum length of an app scope (app id) component.
*
* ⚠️ Same name and same value as `@zion/sdk-cp-dp-contracts`' export, deliberately, and the same
* concept. Contracts may not import cp-dp (SDK-002), so this is the `MAX_VERSION_SLUG_LENGTH`
* precedent one file over (`app-admin-inputs.ts`): two declarations, pinned by a value assertion on
* both sides. **If you change one, change both** — the cross-package equality is asserted in
* `@zion/zion-operator`, which is the nearest package that imports both.
*/
const MAX_APP_ID_LENGTH = 30;
/** The one reserved subject segment for the work namespace. */
const WORK_SUBJECT_SEGMENT = "work";
/**
* The envelope version token. ⛔ The ONLY place a work-envelope version is written.
*
* ⚠️ This is the ENVELOPE's version, a fixed literal. It is not, and must never become, the app's
* version, its `versionSlug`, its image ref or its release id — none of those may reach this
* module at all. The fuse in `__tests__/work-subjects.test.ts` is what holds that.
*/
const WORK_ENVELOPE_VERSION_TOKEN = "v1";
/**
* The durable-name separator. ⛔ **TWO HYPHENS, AND THIS IS A PROOF, NOT A PREFERENCE.**
*
* `ID_PART_PATTERN` (`./id-grammar.ts`) forbids CONSECUTIVE hyphens — its `-(?=[a-z0-9])`
* lookahead requires every hyphen to be followed immediately by an alphanumeric. Therefore `--`
* cannot occur inside an `appScope` or a `fixtureId`. Therefore splitting a durable name on `--`
* is unambiguous: element 0 is the appScope, element 1 the fixtureId, element 2 the literal
* `work`, and everything after is the target (which MAY itself contain `--`, harmlessly, because
* it is the tail).
*
* ⛔ A SINGLE-HYPHEN JOIN WOULD NOT BE INJECTIVE, and here is the counter-example, kept because a
* reader who sees only *"use a separator"* will normalise a later tidy-up to `-` and it will pass
* every test that does not happen to use a hyphenated fixture id:
*
*     (fixtureId: 'a',   target: 'b-c')  ->  app-a-b-c
*     (fixtureId: 'a-b', target: 'c')    ->  app-a-b-c      <-- the SAME name
*
* Two distinct targets, one durable, one cursor. That is H-poolid arriving through the name
* instead of through the hash: *two same-named targets silently merge*.
*/
const WORK_DURABLE_DELIMITER = "--";
/** Quote a value in an error message without letting a hostile string forge structure. */
function quoted(value) {
	return JSON.stringify(value);
}
/**
* The work durable name: `<appScope>--<fixtureId>--work--<target>`.
*
* ⛔ **THE SEPARATOR IS `--` AND THE INJECTIVITY PROOF IS ON {@link WORK_DURABLE_DELIMITER}.**
* Read it before changing anything here; the load-bearing sentence is that `ID_PART_PATTERN`
* (`./id-grammar.ts`) forbids consecutive hyphens, therefore `--` cannot occur inside `appScope`
* or `fixtureId`, therefore the split is unambiguous. Cited so the next reader can CHECK the
* premise rather than trust it.
*
* **Length.** Worst case `30 + 2 + 24 + 2 + 4 + 2 + 40 = 104`. `nats`' own `validateDurableName`
* bans only `.` `*` `>` `/` `\` and whitespace and imposes no length bound; 104 is far below the
* server's 255-byte name limit. {@link assertValidDurableName} is applied to the result anyway,
* the same belt-and-braces the worker helpers use.
*/
function workTargetDurableName(appScope, fixtureId, target) {
	const D = "--";
	const name = `${appScope}${D}${fixtureId}${D}${WORK_SUBJECT_SEGMENT}${D}${target}`;
	assertValidDurableName(name);
	return name;
}
/**
* Derive one target's JetStream coordinates.
*
* ⛔ Throws (never returns a partial route) on any input that fails validation. The checks run in
* this order, and each quotes the offending value:
*
*   1. `appScope`  — {@link ID_PART_PATTERN} and `<= MAX_APP_ID_LENGTH`.
*   2. `fixtureId` — {@link ID_PART_PATTERN} and `<= MAX_FIXTURE_ID_LENGTH`.
*   3. `target`    — {@link WORK_NAME_PATTERN}, ⛔ IMPORTED from the leaf `./work-name.ts` and
*      never restated. `fixture-manifest.ts` already records that restating a grammar is how two
*      copies drift.
*   4. `target === 'work'` is deliberately **NOT** refused. It renders
*      `zion.<app>.work.<fixture>.work.v1`, which is unambiguous. Stated so nobody "fixes" it.
*   5. {@link assertValidDurableName} on the composed name.
*/
function workTargetRoute(ref) {
	const { appScope, fixtureId, target } = ref;
	if (!ID_PART_PATTERN.test(appScope) || appScope.length > 30) throw new Error(`workTargetRoute: appScope ${quoted(appScope)} is not a legal id part of at most 30 characters`);
	if (!ID_PART_PATTERN.test(fixtureId) || fixtureId.length > 24) throw new Error(`workTargetRoute: fixtureId ${quoted(fixtureId)} is not a legal id part of at most 24 characters`);
	if (!WORK_NAME_PATTERN.test(target)) throw new Error(`workTargetRoute: target ${quoted(target)} is not a legal work-unit name`);
	const stream = eventStreamName(appScope);
	const prefix = `zion.${appScope}.${WORK_SUBJECT_SEGMENT}.${fixtureId}.${target}`;
	return {
		stream,
		durable: workTargetDurableName(appScope, fixtureId, target),
		filterSubject: `${prefix}.>`,
		publishSubject: `${prefix}.v1`
	};
}
/**
* Do two concrete NATS subject patterns select any common subject?
*
* Token-wise: `*` matches exactly one token, `>` matches one-or-more remaining tokens, otherwise
* the tokens must be equal. Pure, total and symmetric.
*
* ⛔ NOT a subject VALIDATOR. It answers one question — could a message exist that both patterns
* select — and it is the collision detector's predicate, nothing else.
*/
function subjectsOverlap(a, b) {
	const ta = a.split(".");
	const tb = b.split(".");
	const shared = Math.min(ta.length, tb.length);
	for (let i = 0; i < shared; i += 1) {
		const x = ta[i];
		const y = tb[i];
		if (x === ">" || y === ">") return true;
		if (x !== "*" && y !== "*" && x !== y) return false;
	}
	return ta.length === tb.length;
}
/**
* {@link WorkEnvelopeV1} on the wire.
*
* `runId`'s 128-character cap is the bound the retired pool-runner result wire carried, not a
* second grammar: the minted ids are far shorter.
*/
const workEnvelopeV1Schema = z.object({
	runId: z.string().min(1).max(128),
	payload: z.unknown()
}).strict();
//#endregion
export { MAX_APP_ID_LENGTH, WORK_DURABLE_DELIMITER, WORK_ENVELOPE_VERSION_TOKEN, WORK_SUBJECT_SEGMENT, subjectsOverlap, workEnvelopeV1Schema, workTargetDurableName, workTargetRoute };

//# sourceMappingURL=work-subjects.js.map